import {
  useGetGeolocalizzazioneComuni,
  type Provincia,
} from '@framework/api-client';
import {
  getWidgetProps,
  useExpressionValue,
  type IWidgetComponentProps,
} from '@framework/engine';
import { useField } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import type { Maybe } from '@framework/validation';
import { Alert } from 'antd';
import { useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { ProvinceWidgetSelectComponent } from './components/ProvinceWidgetSelectComponent';
import {
  getProvinceDataFieldName,
  getProvinceFieldName,
  getTownDataFieldName,
  getTownFieldName,
} from './createProvinceWidgetSchema';
import type { IProvinceWidget } from './IProvinceWidget';
import { mapProvinceList } from './utils/mapProvinceList';
import { ProvinceWidgetLogic } from './utils/ProvinceWidgetLogic';

export interface IProvinceWidgetComponentProps
  extends IWidgetComponentProps<IProvinceWidget> {}

export function ProvinceWidgetComponent(props: IProvinceWidgetComponentProps) {
  const { data, isLoading, error } = useGetGeolocalizzazioneComuni();
  const { t } = useTranslation();
  const options = useMemo(() => mapProvinceList(data), [data]);

  const widgetOptions = getWidgetProps(props);

  const defaultProvinceResult = useExpressionValue(
    widgetOptions.name,
    props.widget.defaultProvince,
  );
  const defaultTownResult = useExpressionValue(
    widgetOptions.name,
    props.widget.defaultTown,
  );

  const provinceFieldName = getProvinceFieldName(widgetOptions.name);
  const provinceDataFieldName = getProvinceDataFieldName(widgetOptions.name);
  const townFieldName = getTownFieldName(widgetOptions.name);
  const townDataFieldName = getTownDataFieldName(widgetOptions.name);

  const province = useWatch({ name: provinceFieldName }) as Maybe<string>;
  const provinceDataField = useField<Provincia | null | undefined>(
    provinceDataFieldName,
  );
  const provinceField = useField(provinceFieldName);

  const town = useWatch({ name: townFieldName }) as Maybe<string>;
  const townDataField = useField<Provincia | null | undefined>(
    townDataFieldName,
  );
  const townField = useField(townFieldName);

  const isProvinceDirty = provinceField.dirty.length > 0;
  const isTownDirty = townField.dirty.length > 0;

  const selectedProvinceRef = useRef(province);

  /**
   * Hook che inizializza i dati del widget, andando a caricare
   * i default value, che per questo widget sono campi custom
   * (non utilizza il campo defaultValue come gli altri widget)
   */
  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (error) {
      return;
    }

    // Estraggo i valori
    const computedValues = ProvinceWidgetLogic.getDefaultValue({
      defaultValue: {
        province:
          defaultProvinceResult.type === 'value' &&
          typeof defaultProvinceResult.result === 'string'
            ? defaultProvinceResult.result
            : provinceField.getValue(),
        town:
          defaultTownResult.type === 'value' &&
          typeof defaultTownResult.result === 'string'
            ? defaultTownResult.result
            : townField.getValue(),
      },
      current: {
        province: provinceField.getValue(),
        town: townField.getValue(),
      },
      options,
    });

    if (
      computedValues.province &&
      computedValues.province.codiceBelfiore !== province &&
      !isProvinceDirty
    ) {
      provinceField.setValue(computedValues.province.codiceBelfiore, {
        shouldDirty: false,
      });
      provinceDataField.setValue(computedValues.province, {
        shouldDirty: false,
      });
      selectedProvinceRef.current = computedValues.province.codiceBelfiore;
    }

    if (
      computedValues.town &&
      computedValues.town.codiceBelfiore !== town &&
      !isTownDirty
    ) {
      townField.setValue(computedValues.town.codiceBelfiore, {
        shouldDirty: false,
      });
      townDataField.setValue(computedValues.town, { shouldDirty: false });
    }

    if (
      selectedProvinceRef.current !== computedValues.province?.codiceBelfiore
    ) {
      townField.setValue(null, { shouldDirty: false });
      selectedProvinceRef.current = province;
    }
  }, [
    error,
    isLoading,
    options,
    options.provinces,
    options.towns,
    province,
    provinceDataField,
    provinceField,
    isProvinceDirty,
    isTownDirty,
    defaultProvinceResult,
    defaultTownResult,
    town,
    townDataField,
    townField,
  ]);

  if (error) {
    return (
      <Alert
        message={t('province-widget.error', 'Errore')}
        description={t(
          'province-widget.error-description',
          'Errore durante il caricamento dei dati delle province. Riprovare più tardi.',
        )}
        type="error"
        showIcon
      />
    );
  }

  return (
    <ProvinceWidgetSelectComponent
      {...props}
      loading={isLoading}
      provinces={options.provinces}
      towns={options.towns}
    />
  );
}
